import { useCallback, useMemo } from 'react';
import { DateTime, Duration, DateObjectUnits } from 'luxon';
import { UseFormReturn, useWatch } from 'react-hook-form';
import { TimeSignalClassification } from 'graphql/types';
import { useFeatureFlag } from 'modules/common/hooks';
import { NOTE_FIELDS } from '../../common/noteSchema';

export interface AddNoteFormChangeHandlers {
  handleEndTime(date: DateTime | null): void;
  handleStartTime(date: DateTime | null): void;
  handleDuration(duration: Duration | null, showStartEndTime?: boolean): void;
  onChangeClassification(classification: TimeSignalClassification | null): void;
  resetTimeRange: () => void;
  resetDuration: () => void;
}

export const useFormChangeHandlers = (
  formMethods: UseFormReturn
): AddNoteFormChangeHandlers => {
  const {
    control,
    getValues,
    setValue,
    trigger: triggerValidation
  } = formMethods;
  const isTiwDurationIndependentStartEndTime = useFeatureFlag(
    'isTiwDurationIndependentStartEndTime'
  );
  const date = useWatch({
    control,
    name: 'date'
  });

  const noteDate = useMemo(
    () => (date ? DateTime.fromJSDate(date) : undefined),
    [date]
  );
  const sourceDateUnits = useMemo(() => {
    const units: DateObjectUnits = {
      ...(noteDate
        ? {
            day: noteDate.day,
            month: noteDate.month,
            year: noteDate.year
          }
        : {})
    };

    return units;
  }, [noteDate]);

  const handleStartTime = useCallback(
    (date: DateTime | null) => {
      setValue(
        NOTE_FIELDS.START_TIME,
        date
          ? date
              .set({ ...sourceDateUnits, second: 0, millisecond: 0 })
              .toJSDate()
          : null,
        { shouldDirty: true }
      );
      isTiwDurationIndependentStartEndTime;
      if (!isTiwDurationIndependentStartEndTime) {
        setValue(NOTE_FIELDS.DURATION, 0);
      }
      triggerValidation();
    },
    [
      setValue,
      sourceDateUnits,
      isTiwDurationIndependentStartEndTime,
      triggerValidation
    ]
  );

  const handleEndTime = useCallback(
    (date: DateTime | null) => {
      setValue(
        NOTE_FIELDS.END_TIME,
        date
          ? date
              .set({ ...sourceDateUnits, second: 0, millisecond: 0 })
              .toJSDate()
          : null,
        { shouldDirty: true }
      );
      if (!isTiwDurationIndependentStartEndTime) {
        setValue(NOTE_FIELDS.DURATION, 0);
      }
      triggerValidation();
    },
    [
      setValue,
      sourceDateUnits,
      isTiwDurationIndependentStartEndTime,
      triggerValidation
    ]
  );

  const onChangeClassification = useCallback(
    (classification: TimeSignalClassification | null) => {
      setValue(
        NOTE_FIELDS.CLASSIFICATION,
        classification ? classification : TimeSignalClassification.Unclassified,
        {
          shouldDirty: getValues()[NOTE_FIELDS.NOTE_ID] ? true : false
        }
      );
    },
    [setValue, getValues]
  );

  const handleDuration = useCallback(
    (duration: Duration | null, showStartEndTime?: boolean) => {
      setValue(
        NOTE_FIELDS.DURATION,
        duration
          ? Math.floor(duration.shiftTo('seconds').toObject().seconds ?? 0)
          : null,
        {
          shouldDirty: true
        }
      );
      if (showStartEndTime === true && !isTiwDurationIndependentStartEndTime) {
        setValue(NOTE_FIELDS.START_TIME, null);
        setValue(NOTE_FIELDS.END_TIME, null);
      }

      triggerValidation();
    },
    [isTiwDurationIndependentStartEndTime, setValue, triggerValidation]
  );

  const resetTimeRange = useCallback(() => {
    setValue(NOTE_FIELDS.START_TIME, null);
    setValue(NOTE_FIELDS.END_TIME, null);
    triggerValidation();
  }, [setValue, triggerValidation]);

  const resetDuration = useCallback(() => {
    setValue(NOTE_FIELDS.DURATION, 0);
    triggerValidation();
  }, [setValue, triggerValidation]);

  return useMemo(
    () => ({
      handleStartTime,
      handleEndTime,
      handleDuration,
      onChangeClassification,
      resetTimeRange,
      resetDuration
    }),
    [
      handleStartTime,
      handleEndTime,
      handleDuration,
      resetTimeRange,
      onChangeClassification,
      resetDuration
    ]
  );
};

export default useFormChangeHandlers;
